import { TransportError, type Transport } from "@tdelivr/api-client";

import type { EarningsView } from "./earnings.ts";
import type { ScanLocation, ScanType, UploadContentType } from "./offline-queue.ts";
import type { ProofRecord } from "./proof.ts";
import type { ReadinessView } from "./shift-readiness.ts";

/**
 * Every request this application makes, in one place, with the reason each one goes where it does.
 *
 * ## Reads go to EXP-003. Commands go to the service that owns them.
 *
 * Document 17 WEB-0743 forbids making every microservice directly callable from a channel, and
 * WEB-0065 asks for cross-service views to be composed server-side. So both reads here are EXP-003's
 * — `shift-readiness` composes six services for one screen, `earnings` composes three — and this
 * application makes no read of its own against the 142.
 *
 * Commands are the other way round, and that is ADR 0024's own position rather than a convenience:
 * _"a BFF that relayed a command would be a proxy, which is the thing Document 17 WEB-0743 forbids
 * wearing a BFF's name"_. EXP-003 publishes three operations and all three are reads. So
 * `goOnline`, `goOffline`, `accept`, `decline` and `viewed` post to DRV-05 and DSP-02, which own
 * the state they change and enforce their own scopes on it.
 *
 * ## The one read that is not EXP-003's, and why it is recorded rather than avoided
 *
 * `POST /v1/drivers/{driverId}/availability/online` requires `expectedVersion`, and **EXP-003
 * publishes no version**: its readiness response carries the presence *gate* and not DRV-05's row.
 * So going online needs the version, and the only place it is published is
 * `GET /v1/drivers/{driverId}/availability`.
 *
 * That makes `currentAvailability` a direct read of a domain service from a channel. It is here
 * deliberately and under a narrow rule: it reads the **same resource** the command then writes,
 * immediately before writing it, solely for that resource's own concurrency token. That is one
 * interaction with one service rather than a composition across several, which is the thing
 * WEB-0743 is about. The alternative was a Go online button that cannot be pressed. Recorded as
 * R-29; the fix is either an `expectedVersion` on EXP-003's readiness response or a command on
 * EXP-003, and the second is what WEB-0743 forbids.
 *
 * ## The second read that is not EXP-003's: DEL-07's proof
 *
 * `8 Proof` draws one proof and its verification state, and EXP-003 publishes no proof read. So
 * `deliveryProof` is `GET /v1/proofs/{proofId}` against DEL-07, and the same narrow rule is claimed
 * for it: **one resource of one service, named by an identifier the application was handed**, not a
 * view composed across several. It is the device reading back the thing the device itself produced,
 * which is the shape [ADR 0025](../../../docs/adr/0025-the-upload-service-gets-a-public-surface-and-its-own-device-scope.md)
 * settled for FIL-02 — a courier's own token spending its own authority on its own artefact, because
 * ADR 0020 forbids an experience component holding authority of its own to spend instead.
 *
 * It is recorded rather than waved through, and with the finding that goes with it: DEL-07's public
 * read is guarded by `delivery-proof:read`, which its own contract describes as "Tenant-owned
 * proof" and which therefore grants a courier's phone sight of every proof in the tenant. That is
 * precisely the defect ADR 0025 declined to accept for `uploads:read`, and the fix is the same
 * shape — an ownership column and a 404. R-33, and until then this application reads only the proof
 * it was given the identifier for.
 *
 * ## FIL-02 and LOG-06, which are commands and therefore direct by the settled rule
 *
 * `6 Offline queue` sends what the phone is holding: scans to LOG-06's `POST /v1/device/scans`, and
 * files through FIL-02's three public operations, published by ADR 0025 behind the one new scope
 * `uploads:self`. Both are commands, so the rule above already decides where they go. `writeObject`
 * is the one request in this application that is **not** a platform request at all — see its own
 * note.
 *
 * ## The six reads the three screens of 8 October added, under the same narrow rule
 *
 * `Vehicle`, `4 To pickup` and `5 Scan` read DRV-04, DEL-03, GEO-02, GEO-04 and LOG-02 directly, and
 * **EXP-003 publishes none of them**: its three operations are shift readiness, the offer stream and
 * earnings, and ADR 0030 is explicit that a BFF is built for the read a channel cannot make rather
 * than for every read a channel makes. Each of the six is one resource of one service named by an
 * identifier this application already holds — an order it was told, the address that order's own
 * field names, the parcel a scan was of, the assignments of the courier the token names, the vehicle
 * one of those assignments names — which is the rule `currentAvailability` and `deliveryProof` are
 * already here under.
 *
 * Two of them were **over-scoped in the service** rather than in this application — `vehicle:read`
 * and `parcel:read`, each bounded by the verified tenant and by nothing else, so a courier's token
 * holding either read every row the tenant owns (R-48). They were the same finding and they were not
 * the same case, which is what
 * [ADR 0038](../../../docs/adr/0038-a-reads-audiences-are-the-ones-its-contract-publishes.md)
 * decided. DRV-04's vehicle has a subject one record away — the assignment that names a courier —
 * so that read gained a `vehicle:self` arm and this application spends it. LOG-02's parcel has none
 * at all: it names a merchant, which is a business party rather than a token subject, and custody
 * lives in LOG-06, so the tenant is the correct bound and is stated with its reason. `parcel:read`
 * is therefore still tenant-wide **by decision**, which is a different sentence from the one R-48
 * wrote. R-51.
 *
 * One of them was **refused by the service although the contract published it** — see
 * `deliveryOrder` — and ADR 0038 settled that too, in the contract's favour.
 */

/**
 * DRV-02's onboarding application, as its read answers one.
 *
 * `reason` is non-null only on an `action_required` step and is the reviewer's own words, which
 * is what the design's `Action needed: the plate isn't readable` row draws. It is shown to the
 * applicant unchanged — assembling it from a code would mean enumerating everything that can be
 * wrong with a photograph, which is not a vocabulary any service here could write honestly.
 */
/**
 * LOG-07's transport manifest, as its reads answer one.
 *
 * `sealId`, `bay` and `parcelCount` are null until the manifest is sealed, and they are one
 * fact between them: what the physical act of sealing produced. ADR 0077.
 */
export interface ManifestRecord {
  readonly manifestId: string;
  readonly tenantId: string;
  readonly originHubId: string;
  readonly destinationHubId: string;
  readonly departureAt: string;
  readonly shipmentIds: readonly string[];
  readonly status: "open" | "sealed" | "handed_off" | "cancelled";
  readonly sealId: string | null;
  readonly bay: string | null;
  readonly parcelCount: number | null;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface OnboardingApplicationRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly applicantSubject: string;
  readonly policyVersion: string;
  readonly jurisdiction: string;
  readonly status: string;
  readonly steps: readonly {
    readonly kind: "identity" | "documents" | "vehicle";
    readonly status: "pending" | "verified" | "action_required";
    readonly referenceId: string | null;
    readonly reason: string | null;
  }[];
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** DRV-05's row, narrowed to the two fields a command needs from it. */
export interface AvailabilityRow {
  readonly presence: "online" | "offline";
  readonly availability: "available" | "unavailable";
  readonly version: number;
  readonly leaseId?: string;
  readonly reasonCode: string;
  readonly dispatchable: boolean;
  readonly fresh: boolean;
}

export interface DriverApi {
  shiftReadiness(input: {
    readonly tenantId: string;
    readonly driverId: string;
    readonly vehicleId?: string;
    readonly productRef?: string;
  }): Promise<ReadinessView>;
  earnings(input: {
    readonly tenantId: string;
    readonly driverId: string;
    readonly metrics: readonly string[];
    readonly windowStart: string;
    readonly windowEnd: string;
    readonly payoutId?: string;
  }): Promise<EarningsView>;
  currentAvailability(driverId: string): Promise<AvailabilityRow>;
  goOnline(driverId: string, expectedVersion: number): Promise<AvailabilityRow>;
  /**
   * Declare the courier available for dispatch, which is a second command and not part of the first.
   *
   * **Without this a courier who goes online is never dispatched.** DRV-05 separates presence from
   * availability on purpose — a driver is `online` while carrying a passenger and `unavailable`
   * at the same time — and `goOnline` therefore always lands on `availability: unavailable` with
   * `reasonCode: AWAITING_AVAILABILITY`. Something has to make the second move, and nothing did:
   * measured on the deployed estate, a provisioned courier took a lease and sat at
   * `online / unavailable` for as long as anyone watched, invisible to dispatch.
   *
   * The lease identifier is required and a lapsed one is refused with `fresh_online_lease_required`,
   * which is why this takes the row `goOnline` returned rather than re-reading: the version and the
   * lease have to be the ones that command produced.
   */
  setAvailable(input: {
    readonly driverId: string;
    readonly expectedVersion: number;
    readonly leaseId: string;
  }): Promise<AvailabilityRow>;
  /**
   * `POST /v1/drivers/{driverId}/availability/heartbeat` — extend the lease before it lapses.
   *
   * **Nothing called this, and the consequence was a courier who looked online and could not be
   * dispatched to.** DRV-05's lease is sixty seconds; this application went online and then sent
   * only positions. So the availability row stopped advancing, and DSP-01 — which bounds
   * `availabilityAge` at the same sixty seconds, because an offer to a courier whose application
   * has stopped reporting goes into silence — filtered the courier out of every ranking from the
   * first minute onward. The roster still showed them `online`, which is why DRV-05 sorts that board
   * stalest-first and calls the ordering a warning rather than a convenience.
   *
   * Takes the row the last command returned, like `setAvailable` and `goOffline`: the lease and the
   * version have to be the ones DRV-05 last issued, and the answer carries the next pair.
   */
  heartbeat(input: {
    readonly driverId: string;
    readonly expectedVersion: number;
    readonly leaseId: string;
  }): Promise<AvailabilityRow>;
  goOffline(input: {
    readonly driverId: string;
    readonly expectedVersion: number;
    readonly leaseId: string;
    readonly reasonCode: string;
  }): Promise<AvailabilityRow>;
  /** `POST /v1/ride-offers/{offerId}/accept`, which the contract marks idempotent. */
  acceptOffer(offerId: string, idempotencyKey: string): Promise<void>;
  /** `POST /v1/ride-offers/{offerId}/reject`. The contract marks this one *not* idempotent. */
  declineOffer(offerId: string): Promise<void>;
  /** `POST /v1/ride-offers/{offerId}/view`, so DSP-02 records that the courier saw it. */
  markOfferViewed(offerId: string, idempotencyKey: string): Promise<void>;
  /**
   * `POST /v1/device/scans` — LOG-06, one queued scan.
   *
   * No `Idempotency-Key`, because the contract marks none and the operation does not need one: the
   * scan's own `scanId` and `sourceSequence` are the deduplication, which is why its 200 publishes
   * `outcome: recorded | duplicate | stale`. Its 201 publishes **no body at all**, so the answer is
   * optional here rather than defaulted — see `offline-queue.ts`.
   */
  ingestScan(body: DeviceScan): Promise<ScanOutcome | undefined>;
  /** `POST /v1/tenants/{tenantId}/uploads` — FIL-02, `uploads:self`, ADR 0025. */
  initiateUpload(input: {
    readonly tenantId: string;
    readonly idempotencyKey: string;
    readonly body: InitiateUpload;
  }): Promise<InitiateUploadResult>;
  /**
   * `PUT` to the grant's own URL, with the grant's own headers and **no bearer token**.
   *
   * The one request in this application that does not go through the platform transport, and it is
   * not an exception to anything: ADR 0025 section 6 says the object bytes do not pass through the
   * gateway, so this URL is the object store's, the grant is the whole authorization, and attaching
   * this courier's access token to it would leak a platform credential to a third party.
   */
  writeObject(input: {
    readonly grant: UploadGrant;
    readonly body: Blob;
    readonly contentType: string;
  }): Promise<void>;
  /** `GET /v1/tenants/{tenantId}/uploads/{uploadId}` — FIL-02. A 404 means "not yours". */
  uploadState(input: {
    readonly tenantId: string;
    readonly uploadId: string;
  }): Promise<UploadRecord>;
  /** `POST /v1/tenants/{tenantId}/uploads/{uploadId}/complete` — FIL-02. No request body. */
  completeUpload(input: {
    readonly tenantId: string;
    readonly uploadId: string;
    readonly idempotencyKey: string;
    readonly expectedVersion: number;
  }): Promise<UploadRecord>;
  /**
   * `PUT /v1/drivers/{driverId}/documents/{documentId}` — DRV-03, `driver-document:write`.
   *
   * **The link that closes a courier's onboarding, and it was missing for one reason only.**
   *
   * A courier's phone could put a scan of a permit in the object store from the day ADR 0025
   * published FIL-02 on `/v1` behind `uploads:self`. What it could not do was **attach** it: a
   * permit is a DRV-03 document, and the only way to write one is this operation, behind a scope
   * no channel client held. So `7 Document expiring`'s designed action `Upload renewed permit`
   * was drawn and refused, and EXP-003's readiness gate blocked every courier on `no_documents`
   * — nobody could enter the dispatch pool, on an estate where every other surface worked.
   *
   * The scope is derived from this call, not granted by hand: `channel-scopes.mjs` reads this
   * application's own AST, so spending `driver-document:write` here is what puts it in the realm.
   * ADR 0032.
   *
   * ## Three values, and where each comes from
   *
   *   - **`documentId`** is the caller's. The operation is a `PUT`, so the identifier is chosen
   *     rather than minted, and the queued capture's own id is used — a `crypto.randomUUID()`
   *     that already survives a reload in `localStorage`. That is what makes a replayed flush
   *     revise the same document instead of creating a second one for the same photograph.
   *   - **`objectReference` and `evidenceSha256`** are FIL-02's, read off the completed upload
   *     record rather than recomputed. The digest the object store verified is the one the
   *     document should cite; computing it again here would be this handset asserting what the
   *     store confirmed.
   *   - **`expectedVersion`** is `0` for a first write. DRV-03's domain compares
   *     `current?.version ?? 0` against it, so zero is the only value that creates — and the
   *     operation's schema allows it, with `minimum: 0` and no `exclusiveMinimum`. The
   *     *decision* operation is the one that requires a positive version, correctly: a document
   *     has to exist before anyone can decide about it.
   *
   * ## `type` is the design's word and not a vocabulary invented here
   *
   * DRV-03's `type` is a free string with no published vocabulary, which is why nothing could be
   * assumed. The design names exactly one document on this flow and names it twice —
   * `15 WP14`'s headline "Uploading your permit" and `1b Can't go online`'s "Your PrDP has
   * expired … until a valid permit is approved" — so `permit` is copied from the design rather
   * than chosen. A picker offering several kinds would be a control the design does not draw,
   * which section 1 of `CLAUDE.md` forbids as plainly as section 2 forbids an invented payload.
   * When the design draws a second kind, this takes it from the capture rather than from here.
   */
  attachDocument(input: {
    readonly driverId: string;
    readonly documentId: string;
    readonly type: string;
    readonly objectReference: string;
    readonly evidenceSha256: string;
    readonly expectedVersion: number;
    readonly idempotencyKey: string;
  }): Promise<DriverDocumentRecord>;
  /** `GET /v1/proofs/{proofId}` — DEL-07, `delivery-proof:read`. R-33. */
  deliveryProof(proofId: string): Promise<ProofRecord>;
  /**
   * `GET /v1/drivers/{driverId}/vehicle-assignments` — DRV-04, `vehicle-assignment:self`.
   *
   * `Vehicle`'s own subject, and the one read in this group whose scope has a self arm written into
   * the route rather than claimed by a channel: the operation compares the stored `driverUserId`
   * against the verified token subject and answers 404 — not 403 — for another courier's
   * `driverId`, "because a 403 would confirm that identifier is real". ADR 0034.
   *
   * One page. `limit` defaults to 50 and the ordering is newest first, so an active assignment is in
   * the first page of any courier who has not held fifty vehicles; `nextAfter` is published and is
   * not followed here, which is said rather than hidden because a fifty-first vehicle would read as
   * a courier with none.
   */
  /**
   * `GET /v1/tenants/{tenantId}/onboarding-applications/{applicationId}` — DRV-02,
   * `driver:onboarding:self`.
   *
   * **DRV-02 had three writes and no read of any kind until ADR 0076.** An application could be
   * created, verified and moved through ten statuses, and the applicant had no operation that
   * would tell them so — including when a reviewer had marked it `action_required`.
   *
   * The self scope is bounded by the stored `applicantSubject`, and another applicant's
   * application answers **404 and not 403**, so this application cannot learn that a given
   * identifier is real by being refused it. ADR 0028's shape, ADR 0025 section 3's refusal.
   */
  /**
   * `GET /v1/tenants/{tenantId}/onboarding-applications` — DRV-02, `driver:onboarding:self`.
   *
   * **The read beside it answers one application and nothing answered which one is mine.** So
   * the onboarding board had to be handed an identifier by something that did not exist, which
   * is not a screen a driver can open. The self scope bounds this page to the caller's own
   * applications, so there is no other applicant's row on it to refuse.
   */
  /**
   * `GET /v1/tenants/{tenantId}/manifests` — LOG-07, `manifest:read`.
   *
   * Sealed manifests only, which is what a courier can collect: an open one is still being
   * loaded and has no seal on it yet. Both reads were public all along — what this screen was
   * actually waiting on was what sealing produces, which ADR 0077 records.
   */
  sealedManifests(input: {
    readonly tenantId: string;
    readonly originHubId?: string;
  }): Promise<readonly ManifestRecord[]>;
  /** `GET /v1/tenants/{tenantId}/manifests/{manifestId}` — LOG-07, `manifest:read`. */
  manifest(input: {
    readonly tenantId: string;
    readonly manifestId: string;
  }): Promise<ManifestRecord>;
  onboardingApplications(input: {
    readonly tenantId: string;
  }): Promise<readonly OnboardingApplicationRecord[]>;
  onboardingApplication(input: {
    readonly tenantId: string;
    readonly applicationId: string;
  }): Promise<OnboardingApplicationRecord>;
  vehicleAssignments(driverId: string): Promise<VehicleAssignmentPage>;
  /**
   * `GET /v1/vehicles/{vehicleId}` — DRV-04, `vehicle:self`.
   *
   * The second half of `Vehicle`: the assignment row carries the plate, class, make, model, colour
   * and year, and the **documents** are only on the vehicle itself. So the screen's three designed
   * document rows need this read, keyed on the `assignments.vehicle.vehicleId` the first read
   * published — one resource of one service named by an identifier the application was handed,
   * which is the same narrow rule `deliveryProof` claims above.
   *
   * **The finding this carried is closed.** R-48 recorded `vehicle:read` as bounded by the verified
   * tenant and by nothing else — the `delivery-proof:read` defect of R-33 in a second service — and
   * said the fix was an ownership test and a 404. That is what ADR 0038 wrote: the route decides one
   * `requireScope` ternary by whether the caller holds the tenant scope, the courier arm compares the
   * verified subject against the **stored** `driverUserId` of an assignment naming the vehicle, and
   * a vehicle no assignment of theirs names is refused exactly as an absent one is. So this
   * application spends the owner scope, and what it reads is unchanged — it was already reading only
   * the vehicle its own assignment names. R-51.
   */
  vehicle(vehicleId: string): Promise<VehicleRecord>;
  /**
   * `GET /v1/tenants/{tenantId}/delivery-orders/{orderId}` — DEL-03, `delivery:order:read`.
   *
   * `4 To pickup`'s first hop: the order publishes `pickupAddressId`, which is what makes the
   * design's `Masaki Mall, loading bay` an address read rather than a label nobody writes (ADR
   * 0035).
   *
   * **The contract said a courier may make this read and the service refused it.** The operation's
   * summary has always been "Read as sender recipient operations or workload", and
   * `orders.ts`'s `get` admitted only `delivery:order:workload`, or the sender or recipient party
   * holding `delivery:order:read` — so a courier holding the two scopes this screen spends was
   * answered 403 `order_actor_denied`. This screen was built to the published contract, because that
   * is what `CLAUDE.md` section 2 points at, and it drew its forbidden state honestly if the service
   * refused. Reported rather than worked around: Q-120.
   *
   * [ADR 0038](../../../docs/adr/0038-a-reads-audiences-are-the-ones-its-contract-publishes.md)
   * settled it **in the contract's favour**, and the deciding argument was this screen's own slider:
   * the transition below requires `expectedVersion`, and the only published per-order source of
   * `order.version` is this read, so the narrow reading made the command uncallable by the audience
   * it publishes. The operation now publishes all three audiences, so a courier reaches this read
   * under `delivery:order:operations` alone. The forbidden state stays drawn and still names both
   * scopes — it is now reached by a token short of them rather than by a service that disagreed with
   * its contract. R-51.
   */
  deliveryOrder(input: {
    readonly tenantId: string;
    readonly orderId: string;
  }): Promise<DeliveryOrderRecord>;
  /**
   * `POST /v1/tenants/{tenantId}/delivery-orders/{orderId}/transitions` — DEL-03,
   * `delivery:order:operations`.
   *
   * `I've arrived`, which the design puts behind a slider. No `Idempotency-Key`: the operation's
   * summary calls itself idempotent and declares **no such parameter**, because `expectedVersion`
   * is the guard. `CLAUDE.md` section 2 says to send the header wherever the spec marks one, and
   * this is the `goOnline` case again.
   */
  transitionDeliveryOrder(input: {
    readonly tenantId: string;
    readonly orderId: string;
    readonly toStatus: DeliveryOrderStatus;
    readonly expectedVersion: number;
    readonly occurredAt: string;
    readonly reason: string;
  }): Promise<DeliveryOrderRecord>;
  /** `GET /v1/addresses/{addressId}` — GEO-02, `address:read`. ADR 0035. */
  address(addressId: string): Promise<AddressRecord>;
  /** `POST /v1/distance-eta` — GEO-04, `distance-eta:query`. The design's `1.2 km · 4 min`. */
  distanceEta(body: DistanceEtaQuery): Promise<DistanceEtaResult>;
  /**
   * `POST /v1/driver-locations/observations` — DRV-06, `driver-location:write`.
   *
   * The design's `background location on`, which is a claim about this device sending its position
   * rather than about a permission. Answered 202 with no body on a first observation and 200 with
   * the stored row on a replay, so the answer is optional here for the reason `ingestScan`'s is.
   */
  recordPosition(body: PositionObservation): Promise<void>;
  /** `GET /v1/tenants/{tenantId}/parcels/{parcelId}` — LOG-02, `parcel:read`. */
  parcel(input: { readonly tenantId: string; readonly parcelId: string }): Promise<ParcelRecord>;
  /**
   * `GET /v1/tenants/{tenantId}/drivers/{driverId}/stop-plans` — TRP-04, `trip:stops:self`.
   *
   * `Multi-stop job`'s subject: the plans this courier is carrying. The read
   * [ADR 0040](../../../docs/adr/0040-a-courier-finds-its-stop-plan-through-a-listing-bounded-by-its-own-subject.md)
   * added, and the second listing on this platform whose ownership bound is the service's rather
   * than this application's restraint — the courier's arm compares each plan's stored
   * `driverUserId` against the verified token subject and answers 404 for another courier's
   * identifier. `GET /v1/drivers/{driverId}/vehicle-assignments` is the first.
   */
  stopPlansForDriver(input: {
    readonly tenantId: string;
    readonly driverId: string;
    readonly limit?: number;
    readonly after?: string;
  }): Promise<StopPlanPage>;
  /**
   * `GET /v1/tenants/{tenantId}/stop-plans/{planId}` — TRP-04, `trip:stops:self`.
   *
   * Reached after a 409 rather than instead of the listing: a transition's own answer carries the
   * new version, so this is only needed when the plan moved underneath the screen and the courier
   * has to be shown what it moved to. `CLAUDE.md` section 2: a check-status action is a read,
   * never a retry of the command.
   */
  stopPlan(input: { readonly tenantId: string; readonly planId: string }): Promise<StopPlanRecord>;
  /**
   * `POST /v1/tenants/{tenantId}/stop-plans/{planId}/stops/{stopId}/transitions` — TRP-04,
   * `trip:stops:driver:transition`.
   *
   * The design's `Arrived at stop 2`. Carries an `Idempotency-Key`, which the route requires, and
   * TRP-04's own digest deliberately excludes `occurredAt` — so a courier on an intermittent
   * connection retrying the arrival they just recorded is replayed rather than told the plan
   * changed, which is the fault that service's migration `0002` was written for.
   */
  transitionStop(input: {
    readonly tenantId: string;
    readonly planId: string;
    readonly stopId: string;
    readonly action: "arrive" | "complete" | "skip";
    readonly expectedVersion: number;
    readonly occurredAt: string;
    readonly reason: string | null;
    readonly idempotencyKey: string;
  }): Promise<StopPlanRecord>;
  /**
   * `GET /v1/tenants/{tenantId}/jobs/{jobId}` — FIL-03, `media-processing:status:read`.
   *
   * `Document upload`'s one platform read, and a narrowed one: the public surface answers a status
   * and a transformation where the workload surface answers the object references, both
   * checksums, the lease and the processor provenance. `docs/OPEN_WORK.md` item 2 is the decision
   * and ADR 0029 is why it had to be a publication rather than a gateway rule.
   */
  mediaProcessingJob(input: {
    readonly tenantId: string;
    readonly jobId: string;
  }): Promise<ProcessingJobStatusRecord>;
}

/* ------------------------------------------------- TRP-04, the multi-stop plan a courier carries */

/** `Stop` on TRP-04's published plan, field for field. */
export interface StopRecord {
  readonly stopId: string;
  readonly sequence: number;
  /** The GEO-02 address this stop is at (ADR 0036), which `address()` resolves. */
  readonly addressId: string;
  readonly instructions: string | null;
  readonly status: "pending" | "arrived" | "completed" | "skipped";
  readonly arrivedAt: string | null;
  readonly completedAt: string | null;
  readonly skipReason: string | null;
}

/** `StopPlan`, field for field. `driverUserId` is optional exactly as the contract has it. */
export interface StopPlanRecord {
  readonly planId: string;
  readonly tenantId: string;
  readonly tripId: string;
  readonly tripVersion: number;
  readonly riderId: string;
  readonly driverId: string;
  readonly driverUserId?: string;
  readonly version: number;
  readonly stops: readonly StopRecord[];
  readonly createdAt: string;
}

/** One page of the courier's plans, paged over `planId` — ADR 0040 says why not over time. */
export interface StopPlanPage {
  readonly plans: readonly StopPlanRecord[];
  readonly nextAfter: string | null;
}

/* ---------------------------------------------------- FIL-03, the blur and glare check's verdict */

/** `ProcessingJobStatus` — the whole of what FIL-03's public surface answers. */
export interface ProcessingJobStatusRecord {
  readonly tenantId: string;
  readonly jobId: string;
  readonly transformation: "image-thumbnail" | "image-normalize" | "image-webp" | "metadata-strip";
  readonly status:
    "queued" | "processing" | "retry_wait" | "succeeded" | "quarantined" | "dead_letter";
  readonly createdAt: string;
  readonly updatedAt: string;
}

/* ------------------------------------------------------------------- DRV-04, the vehicle service */

/** `AssignedVehicle` — the vehicle an assignment row carries, field for field. */
export interface AssignedVehicle {
  readonly vehicleId: string;
  readonly registrationNumber: string;
  readonly vehicleClass: string;
  readonly make?: string;
  readonly model?: string;
  readonly colour?: string;
  readonly modelYear?: number;
  readonly operationalStatus: "inactive" | "active" | "suspended" | "deactivated";
  readonly lifecycleStatus: VehicleLifecycleStatus;
}

/** DRV-04's `lifecycleStatus`, exactly. The design's chip prints one of these words. */
export type VehicleLifecycleStatus =
  "draft" | "submitted" | "under_review" | "active_record" | "rejected" | "archived" | "merged";

/** `VehicleAssignment` — the relationship ADR 0034 made a record so a courier can have several. */
export interface VehicleAssignment {
  readonly assignmentId: string;
  readonly tenantId: string;
  readonly driverId: string;
  readonly driverUserId: string;
  readonly assignmentSource: "driver_claimed" | "fleet_assigned";
  readonly status: "active" | "ended";
  readonly effectiveFrom: string;
  readonly effectiveTo?: string;
  readonly endedReason?: string;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly vehicle: AssignedVehicle;
}

export interface VehicleAssignmentPage {
  readonly assignments: readonly VehicleAssignment[];
  /** The cursor for the next page, or null on the last one. Published, and not followed — see above. */
  readonly nextAfter: string | null;
}

/** `VehicleDocument` — the vehicle's own paperwork, which is not the courier's (DRV-03 holds that). */
export interface VehicleDocument {
  readonly documentId: string;
  readonly documentType: "insurance" | "inspection" | "registration" | "permit" | "other";
  readonly objectReference: string;
  readonly issuedOn?: string;
  readonly expiresOn?: string;
  readonly verificationStatus: "pending" | "approved" | "rejected";
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** `Vehicle`, narrowed to what `Vehicle` draws. `documents` is required and may be empty. */
export interface VehicleRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly registrationNumber: string;
  readonly vehicleClass: string;
  readonly make?: string;
  readonly model?: string;
  readonly colour?: string;
  readonly modelYear?: number;
  readonly operationalStatus: "inactive" | "active" | "suspended" | "deactivated";
  readonly lifecycleStatus: VehicleLifecycleStatus;
  readonly documents: readonly VehicleDocument[];
  readonly version: number;
}

/* ------------------------------------------------- DEL-03, GEO-02, GEO-04, DRV-06 and LOG-02 */

/** DEL-03's `status`, exactly. The lifecycle `accepted → pickup_ready → in_custody → …`. */
export type DeliveryOrderStatus =
  | "accepted"
  | "pickup_ready"
  | "in_custody"
  | "delivery_attempted"
  | "delivered"
  | "cancelled"
  | "failed";

/** One parcel of a delivery order. The only hop from an order to a thing LOG-02 can read. */
export interface DeliveryOrderParcel {
  readonly parcelId: string;
  readonly packageType?: string;
}

export interface DeliveryOrderRecord {
  readonly orderId: string;
  readonly tenantId: string;
  readonly pickupAddressId: string;
  readonly deliveryAddressId: string;
  readonly parcels: readonly DeliveryOrderParcel[];
  readonly status: DeliveryOrderStatus;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** GEO-02's address: the record ADR 0035 gave the platform, read back by its identifier. */
export interface AddressRecord {
  readonly addressId: string;
  readonly tenantId: string;
  readonly formattedAddress: string;
  readonly countryCode: string;
  readonly locality?: string;
  readonly administrativeArea?: string;
  readonly postalCode?: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly accuracyMetres: number;
  readonly precision: "rooftop" | "street" | "locality" | "unknown";
  readonly provider: string;
  readonly language: string;
  readonly resolvedAt: string;
  readonly version: number;
}

/** GEO-04's request body, field for field. `mode` is required and publishes four values. */
export interface DistanceEtaQuery {
  readonly origin: { readonly latitude: number; readonly longitude: number };
  readonly destination: { readonly latitude: number; readonly longitude: number };
  readonly mode: "car" | "motorcycle" | "bicycle" | "walking";
  readonly traffic?: "required" | "preferred" | "none";
}

export interface DistanceEtaResult {
  readonly distanceMetres: number;
  readonly routeDurationSeconds: number;
  readonly trafficAdjustmentSeconds: number;
  readonly durationSeconds: number;
  readonly calculatedAt: string;
  readonly freshUntil: string;
}

/** DRV-06's observation, field for field. Every one of the eight is required. */
export interface PositionObservation {
  readonly sampleId: string;
  readonly driverId: string;
  readonly deviceId: string;
  readonly sequence: number;
  readonly latitude: number;
  readonly longitude: number;
  readonly horizontalAccuracyMetres: number;
  readonly observedAt: string;
}

/** LOG-02's parcel, narrowed to what `5 Scan` reads. */
export interface ParcelRecord {
  readonly parcelId: string;
  readonly tenantId: string;
  readonly merchantId: string;
  readonly externalReference: string;
  readonly description: string;
  readonly weightGrams: number;
  readonly handlingFlags: readonly string[];
  readonly status: "registered" | "sealed" | "retired";
  readonly version: number;
}

/** LOG-06's `DeviceScan` request body, field for field. */
export interface DeviceScan {
  readonly scanId: string;
  readonly shipmentId: string;
  readonly parcelId?: string;
  readonly sourceSequence: number;
  readonly type: ScanType;
  readonly location: ScanLocation;
  readonly occurredAt: string;
}

/** LOG-06's own three words for what it did with a scan. */
export type ScanOutcome = "recorded" | "duplicate" | "stale";

/** FIL-02's `InitiateBody`, field for field. */
export interface InitiateUpload {
  readonly contentType: UploadContentType;
  readonly expectedSizeBytes: number;
  readonly expectedSha256: string;
}

/** FIL-02's short-lived conditional grant — the thing a device exists to write to. */
export interface UploadGrant {
  readonly uploadUrl: string;
  readonly method: "PUT";
  readonly expiresAt: string;
  readonly requiredHeaders: Readonly<Record<string, string>>;
}

/** `DriverDocument` — what DRV-03 answers once a capture is attached to one. */
export interface DriverDocumentRecord {
  readonly tenantId: string;
  readonly driverId: string;
  readonly id: string;
  readonly type: string;
  readonly objectReference: string;
  readonly evidenceSha256?: string;
  readonly issuedOn?: string;
  readonly expiresOn?: string;
  readonly verificationStatus: "pending" | "approved" | "rejected";
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface UploadRecord {
  readonly tenantId: string;
  readonly uploadId: string;
  readonly initiatedBySubject?: string;
  readonly objectReference: string;
  readonly contentType: string;
  readonly expectedSizeBytes: number;
  readonly expectedSha256: string;
  readonly status: "initiated" | "quarantined" | "expired" | "rejected";
  readonly expiresAt: string;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface InitiateUploadResult {
  readonly upload: UploadRecord;
  readonly grant: UploadGrant;
  readonly replay: boolean;
}

/**
 * The reason code this application sends when a courier presses Go offline.
 *
 * DRV-05 requires `reasonCode` on its offline command and types it as a free string, publishing no
 * set of values. This is not an invented field — the field is published and required, and its whole
 * purpose is for the caller to say why — but the *value* is this build's, so it is named here where
 * it can be found and changed rather than written at a call site. Recorded as Q-64.
 */
export const OFFLINE_BY_REQUEST = "driver_requested";

export function createDriverApi(
  transport: Transport,
  /** How the object bytes leave. Injected so a test can watch the grant being honoured. */
  write: typeof globalThis.fetch = globalThis.fetch.bind(globalThis),
): DriverApi {
  return {
    shiftReadiness(input): Promise<ReadinessView> {
      return transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/driver-app/drivers/${encodeURIComponent(input.driverId)}/shift-readiness`,
        query: {
          ...(input.vehicleId === undefined ? {} : { vehicleId: input.vehicleId }),
          ...(input.productRef === undefined ? {} : { productRef: input.productRef }),
        },
      }) as Promise<ReadinessView>;
    },

    earnings(input): Promise<EarningsView> {
      return transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/driver-app/drivers/${encodeURIComponent(input.driverId)}/earnings`,
        query: {
          // Comma-separated because DRV-07 types `metric` as a free string in its path and publishes
          // no set of them, so there is nothing for a repeated parameter to enumerate.
          metrics: input.metrics.join(","),
          windowStart: input.windowStart,
          windowEnd: input.windowEnd,
          ...(input.payoutId === undefined ? {} : { payoutId: input.payoutId }),
        },
      }) as Promise<EarningsView>;
    },

    async currentAvailability(driverId): Promise<AvailabilityRow> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/drivers/${encodeURIComponent(driverId)}/availability`,
      })) as { readonly availability: AvailabilityRow };
      return answer.availability;
    },

    async heartbeat(input): Promise<AvailabilityRow> {
      /*
       * A **fresh** key per beat, where every other availability command derives one.
       *
       * The key is required: DRV-05 calls its own `key()` on `idempotency-key` for this route and
       * answers `400 idempotency_key_required` without it, while the published contract marks no
       * such parameter. That is the identical mismatch the test beside this file documents for
       * `Go online` — the service is stricter than the document describing it — on a second route
       * in the same service. Measured: `400 /v1/drivers/.../availability/heartbeat` on the
       * deployment, from an application that had believed the contract.
       *
       * Fresh rather than derived, which is the one thing about this command worth stating. The
       * other commands derive their key from the driver and the version so that a double tap or a
       * retry asks for *the same* lease and is replayed. A heartbeat is the opposite: its whole
       * purpose is that the next one happens, and DRV-05 extends using its own clock. A derived key
       * would make every beat after the first a replay of the first, so the lease would quietly
       * stop being extended — the exact failure the heartbeat exists to prevent.
       *
       * A 409 is not an error to show a courier either. It means the lease this application holds is
       * stale — somebody went offline, or a second device took the lease — and the caller reloads
       * rather than reporting a failure about a background call nobody asked for.
       */
      const answer = (await transport.request({
        method: "POST",
        path: `/v1/drivers/${encodeURIComponent(input.driverId)}/availability/heartbeat`,
        body: { expectedVersion: input.expectedVersion, leaseId: input.leaseId },
        options: { idempotencyKey: `beat:${globalThis.crypto.randomUUID()}` },
      })) as { readonly availability: AvailabilityRow };
      return answer.availability;
    },

    async goOnline(driverId, expectedVersion): Promise<AvailabilityRow> {
      /*
       * **An `Idempotency-Key`, because DRV-05 requires one — and no driver could go online
       * without it.**
       *
       * This used to send none, and the reasoning was good: the published contract marks this
       * operation with no `Idempotency-Key` parameter, `CLAUDE.md` section 2 says to send the
       * header wherever the spec marks one, and `expectedVersion` already guards a repeat. The
       * contract is simply wrong. `services/driver/driver-availability/src/http` calls its own
       * `key()` on `request.headers["idempotency-key"]` for this route and answers
       * `400 idempotency_key_required` without it, so the service is stricter than the document
       * describing it.
       *
       * Measured by pressing the button on the deployed application: `Go online` answered
       * *"That didn't work — A valid Idempotency-Key header is required"*, every time, for every
       * driver. Nothing in any test saw it, because nothing in any test presses it.
       *
       * Derived from the driver and the version rather than random, which is what makes it a
       * guard rather than a formality: a double tap, or a retry after a timeout the phone could
       * not see the answer to, asks for the same lease and is replayed instead of taking a second
       * one.
       */
      const answer = (await transport.request({
        method: "POST",
        path: `/v1/drivers/${encodeURIComponent(driverId)}/availability/online`,
        body: { expectedVersion },
        options: { idempotencyKey: `online:${driverId}:${String(expectedVersion)}` },
      })) as { readonly availability: AvailabilityRow };
      return answer.availability;
    },

    async setAvailable(input): Promise<AvailabilityRow> {
      /*
       * The same key rule as `goOnline`: derived from the driver and the version being moved
       * from, so a retry after an unanswered timeout replays rather than fighting the version.
       * DRV-05 requires the header on this route too — its contract does not say so, which the
       * seeder recorded as a gap before this application ever hit it.
       */
      const answer = (await transport.request({
        method: "POST",
        path: `/v1/drivers/${encodeURIComponent(input.driverId)}/availability`,
        body: {
          expectedVersion: input.expectedVersion,
          availability: "available",
          reasonCode: "DRIVER_WENT_ONLINE",
          leaseId: input.leaseId,
        },
        options: {
          idempotencyKey: `available:${input.driverId}:${String(input.expectedVersion)}`,
        },
      })) as { readonly availability: AvailabilityRow };
      return answer.availability;
    },

    async goOffline(input): Promise<AvailabilityRow> {
      const answer = (await transport.request({
        method: "POST",
        path: `/v1/drivers/${encodeURIComponent(input.driverId)}/availability/offline`,
        body: {
          expectedVersion: input.expectedVersion,
          leaseId: input.leaseId,
          reasonCode: input.reasonCode,
        },
      })) as { readonly availability: AvailabilityRow };
      return answer.availability;
    },

    async acceptOffer(offerId, idempotencyKey): Promise<void> {
      /*
       * The one command on this application that cannot be undone, which is why the design puts it
       * behind a slider — and the one that must not be retried blindly, which is why it carries a
       * key. The contract marks it idempotent; `CLAUDE.md` section 2 says to send one wherever the
       * spec does.
       */
      await transport.request({
        method: "POST",
        path: `/v1/ride-offers/${encodeURIComponent(offerId)}/accept`,
        body: {},
        options: { idempotencyKey },
      });
    },

    async declineOffer(offerId): Promise<void> {
      // No key, because the contract does not mark this one idempotent. Declining twice is declining.
      await transport.request({
        method: "POST",
        path: `/v1/ride-offers/${encodeURIComponent(offerId)}/reject`,
        body: {},
      });
    },

    async markOfferViewed(offerId, idempotencyKey): Promise<void> {
      await transport.request({
        method: "POST",
        path: `/v1/ride-offers/${encodeURIComponent(offerId)}/view`,
        body: {},
        options: { idempotencyKey },
      });
    },

    async ingestScan(body): Promise<ScanOutcome | undefined> {
      const answer = (await transport.request({
        method: "POST",
        path: "/v1/device/scans",
        body,
      })) as { readonly outcome?: ScanOutcome } | undefined;
      return answer?.outcome;
    },

    initiateUpload(input): Promise<InitiateUploadResult> {
      return transport.request({
        method: "POST",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/uploads`,
        body: input.body,
        options: { idempotencyKey: input.idempotencyKey },
      }) as Promise<InitiateUploadResult>;
    },

    async writeObject(input): Promise<void> {
      /*
       * `fetch` directly, and deliberately not the transport.
       *
       * The transport attaches the bearer token, the tenant header and a JSON content type to every
       * request, and all three would be wrong here: the destination is the object store, the grant's
       * signature is the authorization, and the body is a photograph. `TransportError` is still the
       * class a failure is reported as, so the queue reads a dropped connection here exactly as it
       * reads one against the gateway.
       */
      let response: Response;
      try {
        response = await write(input.grant.uploadUrl, {
          method: input.grant.method,
          headers: { ...input.grant.requiredHeaders, "content-type": input.contentType },
          body: input.body,
        });
      } catch (cause) {
        throw new TransportError("The capture did not reach the object store", cause);
      }
      if (!response.ok) {
        /*
         * The object store is not a platform service and does not answer in the platform's error
         * envelope, so this is reported as a transport failure rather than dressed as an `ApiError`
         * with a code no service ever emitted. The queue keeps the item and tries again.
         */
        throw new TransportError(
          `The object store refused the capture with ${String(response.status)}`,
        );
      }
    },

    async uploadState(input): Promise<UploadRecord> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/uploads/${encodeURIComponent(input.uploadId)}`,
      })) as { readonly upload: UploadRecord };
      return answer.upload;
    },

    async completeUpload(input): Promise<UploadRecord> {
      /*
       * `If-Match` carries the version, which is what FIL-02's `ExpectedVersion` parameter is: a
       * positive aggregate version in a header rather than a field in a body, and the operation
       * publishes no body at all. Sending one would be inventing a payload.
       */
      const answer = (await transport.request({
        method: "POST",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/uploads/${encodeURIComponent(input.uploadId)}/complete`,
        options: {
          idempotencyKey: input.idempotencyKey,
          headers: { "if-match": String(input.expectedVersion) },
        },
      })) as { readonly upload: UploadRecord };
      return answer.upload;
    },

    async attachDocument(input): Promise<DriverDocumentRecord> {
      const answer = (await transport.request({
        method: "PUT",
        path: `/v1/drivers/${encodeURIComponent(input.driverId)}/documents/${encodeURIComponent(input.documentId)}`,
        body: {
          type: input.type,
          objectReference: input.objectReference,
          evidenceSha256: input.evidenceSha256,
          expectedVersion: input.expectedVersion,
        },
        options: { idempotencyKey: input.idempotencyKey },
      })) as { readonly document: DriverDocumentRecord };
      return answer.document;
    },

    async deliveryProof(proofId): Promise<ProofRecord> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/proofs/${encodeURIComponent(proofId)}`,
      })) as { readonly proof: ProofRecord };
      return answer.proof;
    },

    async sealedManifests(input): Promise<readonly ManifestRecord[]> {
      const query = new URLSearchParams({ status: "sealed" });
      if (input.originHubId !== undefined) query.set("originHubId", input.originHubId);
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/manifests?${query.toString()}`,
      })) as { readonly manifests: readonly ManifestRecord[] };
      return answer.manifests;
    },

    async manifest(input): Promise<ManifestRecord> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/manifests/${encodeURIComponent(input.manifestId)}`,
      })) as { readonly manifest: ManifestRecord };
      return answer.manifest;
    },

    async onboardingApplications(input): Promise<readonly OnboardingApplicationRecord[]> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/onboarding-applications`,
      })) as { readonly applications: readonly OnboardingApplicationRecord[] };
      return answer.applications;
    },

    async onboardingApplication(input): Promise<OnboardingApplicationRecord> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/onboarding-applications/${encodeURIComponent(input.applicationId)}`,
      })) as { readonly application: OnboardingApplicationRecord };
      return answer.application;
    },

    vehicleAssignments(driverId): Promise<VehicleAssignmentPage> {
      /*
       * No `limit` and no `after`.
       *
       * The contract defaults `limit` to 50 and orders newest first, and sending a smaller page
       * would be this application deciding how many vehicles a courier has held. `nextAfter` is read
       * back by the caller and is not followed, which `vehicle.ts` states where it picks the active
       * row.
       */
      return transport.request({
        method: "GET",
        path: `/v1/drivers/${encodeURIComponent(driverId)}/vehicle-assignments`,
      }) as Promise<VehicleAssignmentPage>;
    },

    async vehicle(vehicleId): Promise<VehicleRecord> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/vehicles/${encodeURIComponent(vehicleId)}`,
      })) as { readonly vehicle: VehicleRecord };
      return answer.vehicle;
    },

    async deliveryOrder(input): Promise<DeliveryOrderRecord> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/delivery-orders/${encodeURIComponent(input.orderId)}`,
      })) as { readonly order: DeliveryOrderRecord };
      return answer.order;
    },

    async transitionDeliveryOrder(input): Promise<DeliveryOrderRecord> {
      /*
       * Every field of the body is published and required: `toStatus`, `expectedVersion`,
       * `occurredAt` and `reason`. The *values* of the last two are this application's — the instant
       * the courier pressed the slider, and a reason code — and are decided at the call site in
       * `pickup.ts` where they can be found, which is the arrangement `OFFLINE_BY_REQUEST` above
       * already uses. Nothing is added to the body.
       */
      const answer = (await transport.request({
        method: "POST",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/delivery-orders/${encodeURIComponent(input.orderId)}/transitions`,
        body: {
          toStatus: input.toStatus,
          expectedVersion: input.expectedVersion,
          occurredAt: input.occurredAt,
          reason: input.reason,
        },
      })) as { readonly order: DeliveryOrderRecord };
      return answer.order;
    },

    async address(addressId): Promise<AddressRecord> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/addresses/${encodeURIComponent(addressId)}`,
      })) as { readonly address: AddressRecord };
      return answer.address;
    },

    async distanceEta(body): Promise<DistanceEtaResult> {
      const answer = (await transport.request({
        method: "POST",
        path: "/v1/distance-eta",
        body,
      })) as { readonly result: DistanceEtaResult };
      return answer.result;
    },

    async recordPosition(body): Promise<void> {
      /*
       * No key, and no answer read.
       *
       * GEO-04 and DRV-06 both declare no `Idempotency-Key`: the observation's own `sampleId` and
       * `sequence` are the deduplication, which is why its 200 is described as an idempotent replay
       * and its 202 publishes no body at all. A screen that read `advancedLatest` off a 202 would be
       * reading a field that is not there.
       */
      await transport.request({
        method: "POST",
        path: "/v1/driver-locations/observations",
        body,
      });
    },

    async parcel(input): Promise<ParcelRecord> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/parcels/${encodeURIComponent(input.parcelId)}`,
      })) as { readonly parcel: ParcelRecord };
      return answer.parcel;
    },

    stopPlansForDriver(input): Promise<StopPlanPage> {
      return transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/drivers/${encodeURIComponent(input.driverId)}/stop-plans`,
        query: {
          ...(input.limit === undefined ? {} : { limit: String(input.limit) }),
          ...(input.after === undefined ? {} : { after: input.after }),
        },
      }) as Promise<StopPlanPage>;
    },

    async stopPlan(input): Promise<StopPlanRecord> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/stop-plans/${encodeURIComponent(input.planId)}`,
      })) as { readonly plan: StopPlanRecord };
      return answer.plan;
    },

    async transitionStop(input): Promise<StopPlanRecord> {
      const answer = (await transport.request({
        method: "POST",
        path:
          `/v1/tenants/${encodeURIComponent(input.tenantId)}/stop-plans/${encodeURIComponent(input.planId)}` +
          `/stops/${encodeURIComponent(input.stopId)}/transitions`,
        body: {
          action: input.action,
          expectedVersion: input.expectedVersion,
          occurredAt: input.occurredAt,
          reason: input.reason,
        },
        options: { idempotencyKey: input.idempotencyKey },
      })) as { readonly plan: StopPlanRecord };
      return answer.plan;
    },

    async mediaProcessingJob(input): Promise<ProcessingJobStatusRecord> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/jobs/${encodeURIComponent(input.jobId)}`,
      })) as { readonly job: ProcessingJobStatusRecord };
      return answer.job;
    },
  };
}
